export type WidgetLayout = {
  width: 6 | 12;
  position: number;
};

export type DashboardParameter =
  | {
      id: string;
      type: "string" | "number" | "boolean" | "date";
      label: string;
      hidden?: boolean;
      default?: string | number | boolean | null;
    }
  | {
      id: string;
      type: "text";
      label: string;
      hidden?: boolean;
      default?: string | null;
      /** Optional placeholder for the text input. */
      placeholder?: string;
    }
  | {
      id: string;
      type: "select";
      label: string;
      options: { value: string; label: string }[];
      hidden?: boolean;
      default?: string | null;
    }
  | {
      id: string;
      type: "date_range";
      label: string;
      hidden?: boolean;
      default?: { preset?: "last_7_days" | "last_30_days" | "last_90_days" | "all" } | null;
    };

export type DashboardParameterValue =
  | string
  | number
  | boolean
  | null
  | { preset?: "last_7_days" | "last_30_days" | "last_90_days" | "all" };

export type WidgetInteractionPointContext = {
  /** Stacked bar / chart series key (matches a `config.y` entry). */
  seriesKey?: string;
};

export type WidgetInteraction = {
  event: "row_click" | "point_click";
  /** When empty, rely on `open_url` only (typical for `point_click`). */
  set_parameters?: Record<string, string>;
  /** Optional navigation template (supports `{{param}}`, `$row.col`, `$point.series`, `$point.rating_value`). */
  open_url?: string | null;
  open_url_target?: "_self" | "_blank";
};

type WidgetCommon = {
  description?: string;
  requires?: string[];
  interactions?: WidgetInteraction[];
};

export type WidgetValueFormat =
  | "number"
  | "compact"
  | "percent"
  | "percent_fraction"
  | "currency"
  | "duration_ms"
  | "duration_seconds"
  | "bytes"
  | "auto";

export type WidgetDeltaPolarity = "increase_is_good" | "decrease_is_good" | "neutral";

export type StatWithDeltaConfig = {
  value: string;
  compare_value?: string;
  format?: WidgetValueFormat;
  delta_polarity?: WidgetDeltaPolarity;
  compare_label?: string;
};

export type SparklineStatConfig = StatWithDeltaConfig & {
  series: { x: string; y: string };
};

export type StatStripCellConfig = {
  id: string;
  label: string;
  value: string;
  compare_value?: string;
  format?: WidgetValueFormat;
  delta_polarity?: WidgetDeltaPolarity;
};

export type StatStripConfig = {
  cells: StatStripCellConfig[];
  rows_as_cells?: boolean;
  compare_label?: string;
};

export type EntityPickerMetricConfig = {
  id: string;
  label: string;
  value: string;
  format?: WidgetValueFormat;
};

export type EntityPickerConfig = {
  /** Column whose value is bound to the target parameter on click. */
  value: string;
  /** Column rendered as the primary card label. */
  label: string;
  /** Optional secondary line under the label (e.g. email). */
  sublabel?: string;
  /** Optional metrics shown below the label (max 3 displayed). */
  metrics?: EntityPickerMetricConfig[];
  /** Sort by this column; defaults to first metric's value column. */
  sort_by?: string;
  /** Default desc — leaderboards usually rank by primary metric descending. */
  sort_dir?: "asc" | "desc";
  /** Cards per row at full width; defaults to 4. */
  columns?: 2 | 3 | 4;
};

export type DashboardWidget =
  | ({
      id: string;
      type: "big_number";
      title: string;
      sql: string;
      layout: WidgetLayout;
      config: { value: string; format?: "number" | "percent" | "currency" };
    } & WidgetCommon)
  | ({
      id: string;
      type: "line";
      title: string;
      sql: string;
      layout: WidgetLayout;
      config: { x: string; y: string[] };
    } & WidgetCommon)
  | ({
      id: string;
      type: "table";
      title: string;
      sql: string;
      layout: WidgetLayout;
      config: { hidden_columns?: string[] };
    } & WidgetCommon)
  | ({
      id: string;
      type: "markdown";
      title?: string;
      layout: WidgetLayout;
      config: { text: string };
    } & WidgetCommon)
  | ({
      id: string;
      type: "stat_with_delta";
      title: string;
      sql: string;
      layout: WidgetLayout;
      config: StatWithDeltaConfig;
    } & WidgetCommon)
  | ({
      id: string;
      type: "sparkline_stat";
      title: string;
      sql: string;
      layout: WidgetLayout;
      config: SparklineStatConfig;
    } & WidgetCommon)
  | ({
      id: string;
      type: "stat_strip";
      title: string;
      sql: string;
      layout: WidgetLayout;
      config: StatStripConfig;
    } & WidgetCommon)
  | ({
      id: string;
      type: "calendar_heatmap";
      title: string;
      sql: string;
      layout: WidgetLayout;
      config: {
        date: string;
        value: string;
        value_format?: WidgetValueFormat;
        weeks?: number;
        start_day?: "sunday" | "monday";
      };
    } & WidgetCommon)
  | ({
      id: string;
      type: "bar";
      title: string;
      sql: string;
      layout: WidgetLayout;
      config: {
        x: string;
        y: string[];
        orientation?: "vertical" | "horizontal";
        stacked?: boolean;
        value_format?: WidgetValueFormat;
      };
    } & WidgetCommon)
  | ({
      id: string;
      type: "stacked_area";
      title: string;
      sql: string;
      layout: WidgetLayout;
      config: {
        x: string;
        y: string[];
        value_format?: WidgetValueFormat;
      };
    } & WidgetCommon)
  | ({
      id: string;
      type: "heatmap";
      title: string;
      sql: string;
      layout: WidgetLayout;
      config: {
        x: string;
        y: string;
        value: string;
        value_format?: WidgetValueFormat;
        cell_min_width?: number;
      };
    } & WidgetCommon)
  | ({
      id: string;
      type: "histogram";
      title: string;
      sql: string;
      layout: WidgetLayout;
      config: {
        bin: string;
        count: string;
        bin_width?: number;
        value_format?: WidgetValueFormat;
        bin_format?: WidgetValueFormat;
      };
    } & WidgetCommon)
  | ({
      id: string;
      type: "entity_picker";
      title: string;
      sql: string;
      layout: WidgetLayout;
      config: EntityPickerConfig;
    } & WidgetCommon);

export type DashboardDefinition = {
  parameters?: DashboardParameter[];
  widgets: DashboardWidget[];
};

export type Dashboard = {
  id: string;
  owner_id: string;
  title: string;
  description?: string | null;
  is_public: boolean;
  definition: DashboardDefinition;
  created_at: string;
  updated_at: string;
};

export type WidgetDataRow = Record<string, string | number | boolean | null>;

export type WidgetDataResult = {
  columns: string[];
  rows: WidgetDataRow[];
  isLoading: boolean;
  error?: Error | null;
};

export type CreateDashboardInput = {
  title: string;
  description?: string;
  is_public?: boolean;
  definition: DashboardDefinition;
};

export type UpdateDashboardInput = Partial<CreateDashboardInput> & {
  id: string;
};

export function sortWidgetsByPosition(widgets: DashboardWidget[]): DashboardWidget[] {
  return [...widgets].sort((a, b) => a.layout.position - b.layout.position);
}

/**
 * Extract every `:name` placeholder referenced by a dashboard SQL string.
 *
 * Used to compute a per-widget params slice for React Query cache keys so
 * that updating an unrelated parameter (e.g. a row-click drilldown that
 * sets `:collector_id`) doesn't bust every widget's cache. The regex
 * mirrors the server-side `sqlglot` placeholder grammar: `:` followed by
 * an identifier (letter/underscore start, then alnum/underscore), and
 * NOT preceded by another `:` so we don't match Postgres `::` casts.
 *
 * String literals are stripped before scanning so a value like
 * `where label = 'status:active'` doesn't yield a false-positive
 * `active` parameter reference. We only need approximate accuracy for
 * cache-keying — the server-side AST parser is the source of truth.
 */
export function extractSqlParamNames(sql: string): string[] {
  // Strip Postgres single-quoted string literals (with `''` escaping) so
  // colons inside them don't get mis-parsed as parameter references.
  const stripped = sql.replace(/'(?:[^']|'')*'/g, "");
  const seen = new Set<string>();
  const re = /(^|[^:\w]):([a-zA-Z_][a-zA-Z0-9_]*)/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(stripped)) !== null) seen.add(match[2]);
  return Array.from(seen);
}

/**
 * SQL bind names that are still unset for the purpose of firing `/sql-query`.
 * `null` is treated as a deliberate bind (e.g. `WHERE (:id IS NULL OR …)`);
 * only `undefined` and `""` block the request (typical “not initialized yet”).
 */
export function missingSqlBindParamNames(
  sql: string,
  params: Record<string, DashboardParameterValue> | undefined,
): string[] {
  return extractSqlParamNames(sql).filter((name) => {
    const value = params?.[name];
    return value === undefined || value === "";
  });
}

/**
 * Resolve a value from an interaction `set_parameters` expression.
 *
 * - `"$row.colname"` reads `row[colname]` and falls back to `null` if the
 *   column is absent (so a misnamed binding produces a clear empty filter
 *   rather than a stringified `"$row.colname"` accidentally bound as a
 *   parameter value).
 * - Anything else is treated as a literal value, allowing widget authors
 *   to hard-code parameter values inside `set_parameters`.
 */
export function resolveRowValueExpression(
  expression: string,
  row: WidgetDataRow,
  point?: WidgetInteractionPointContext,
): DashboardParameterValue {
  if (expression.startsWith("$point.")) {
    const key = expression.slice("$point.".length);
    if (key === "series") return point?.seriesKey ?? null;
    if (key === "rating_value") {
      const sk = point?.seriesKey;
      if (!sk) return null;
      const map: Record<string, number> = { perfect: 5, partial: 3, fail: 0 };
      return map[sk] ?? null;
    }
    return null;
  }
  if (!expression.startsWith("$row.")) return expression;
  const key = expression.slice("$row.".length);
  return row[key] ?? null;
}
